// components/ImagePeek.jsx
// ─────────────────────────────────────────────────────────────────────────────
// Look at a gallery thumbnail properly, without leaving the form.
//
// ASKED FOR 27 Sep 2026: "on hovering over any image in the multiple images,
// we should have an expanded image." The extra-angles tiles are 96px squares;
// on a microphone shot that is enough to tell there IS a photo and not enough
// to tell whether it is the right one, in focus, or the fifth near-duplicate.
//
// TWO CONTROLS, NOT ONE, AND DELIBERATELY SO
//
//   HOVER gives a PREVIEW — a floating panel, no backdrop, nothing to dismiss.
//   It is a glance.
//
//   CLICK gives the MODAL — backdrop, Esc, arrow keys, a counter. It is a
//   look, and you can step through every angle without going back to the strip.
//
// A focus-trapping modal on hover would be the wrong control for the glance:
// these tiles are DRAGGABLE, so a modal would open the instant a reorder drag
// began; it would cover the strip you are comparing against; and it can only
// be closed deliberately, so passing the mouse over a tile would cost you a
// dismissal. The preview costs nothing to open or to leave.
//
// WHY A PORTAL. The strip's grid is `overflow-y: auto` and each tile is
// `overflow: hidden` — an in-flow preview is clipped twice over, which is what
// makes the obvious implementation render a sliver and look broken.
// ─────────────────────────────────────────────────────────────────────────────

import React, { useCallback, useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { Modal } from "react-bootstrap";
import { MdChevronLeft, MdChevronRight, MdClose } from "react-icons/md";

const PREVIEW = 320; // px, the floating panel's longest side
const GAP = 12; // space between the tile and the panel
const OPEN_DELAY_MS = 140;

/**
 * Where to put the panel: above the anchor when there is room, below when
 * there is not, clamped so it never leaves the viewport. Returning fixed
 * coordinates rather than a transform keeps it out of every clipping ancestor.
 */
export const placePreview = (rect, viewport, size = PREVIEW, gap = GAP) => {
  if (!rect) return null;
  const above = rect.top - gap - size;
  const below = rect.bottom + gap;
  const top = above >= 8 ? above : Math.min(below, viewport.height - size - 8);

  const centred = rect.left + rect.width / 2 - size / 2;
  const left = Math.max(8, Math.min(centred, viewport.width - size - 8));

  return { top: Math.max(8, top), left };
};

/** The glance. Renders nothing without an anchor. */
export const ImageHoverPreview = ({ src, rect, alt = "" }) => {
  if (!src || !rect || typeof document === "undefined") return null;

  const pos = placePreview(rect, {
    width: window.innerWidth,
    height: window.innerHeight,
  });
  if (!pos) return null;

  return ReactDOM.createPortal(
    <div
      className="lk-peek"
      style={{ top: pos.top, left: pos.left, width: PREVIEW, height: PREVIEW }}
      aria-hidden="true"
    >
      <img src={src} alt={alt} className="lk-peek-img" />
    </div>,
    document.body
  );
};

/** The look. `index` of null keeps it closed. */
export const ImageLightbox = ({ images = [], index, onClose, onIndex, alt }) => {
  const open = index !== null && index !== undefined && images.length > 0;
  const safeIndex = open ? Math.max(0, Math.min(index, images.length - 1)) : 0;

  const step = useCallback(
    (delta) => {
      if (!open || images.length < 2) return;
      const next = (safeIndex + delta + images.length) % images.length;
      if (onIndex) onIndex(next);
    },
    [open, images.length, safeIndex, onIndex]
  );

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "ArrowLeft") step(-1);
      if (e.key === "ArrowRight") step(1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, step]);

  if (!open) return null;

  return (
    <Modal show onHide={onClose} centered size="lg" className="lk-lightbox">
      <div className="lk-lightbox-body">
        <button
          type="button"
          className="lk-lightbox-close"
          onClick={onClose}
          aria-label="Close"
        >
          <MdClose size={20} />
        </button>

        {images.length > 1 && (
          <button
            type="button"
            className="lk-lightbox-nav lk-lightbox-prev"
            onClick={() => step(-1)}
            aria-label="Previous image"
          >
            <MdChevronLeft size={26} />
          </button>
        )}

        <img
          src={images[safeIndex]}
          alt={alt ? `${alt} ${safeIndex + 1}` : `Image ${safeIndex + 1}`}
          className="lk-lightbox-img"
        />

        {images.length > 1 && (
          <button
            type="button"
            className="lk-lightbox-nav lk-lightbox-next"
            onClick={() => step(1)}
            aria-label="Next image"
          >
            <MdChevronRight size={26} />
          </button>
        )}
      </div>
      {images.length > 1 && (
        <div className="lk-lightbox-count">
          {safeIndex + 1} of {images.length}
        </div>
      )}
    </Modal>
  );
};

/**
 * The state behind both, so a gallery renders ONE preview and ONE modal
 * however many tiles it has.
 *
 * `suppressed` turns the hover preview off wholesale — the strip passes its
 * drag state, because a preview that opens mid-reorder is in the way of the
 * gesture that opened it.
 */
export const useImagePeek = ({ suppressed = false } = {}) => {
  const [hover, setHover] = useState(null); // { index, rect }
  const [lightbox, setLightbox] = useState(null); // index | null
  const timerRef = useRef(null);

  const clearTimer = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };

  const close = useCallback(() => {
    clearTimer();
    setHover(null);
  }, []);

  useEffect(() => {
    if (suppressed) close();
  }, [suppressed, close]);

  // A fixed panel anchored to a tile drifts away from it the moment anything
  // scrolls, so it goes rather than lies about what it is showing.
  useEffect(() => {
    if (!hover) return undefined;
    window.addEventListener("scroll", close, true);
    return () => window.removeEventListener("scroll", close, true);
  }, [hover, close]);

  useEffect(() => clearTimer, []);

  /* `openRef` rather than reading `hover`: this callback is handed to every
     tile, and depending on `hover` would rebuild all of them on each move
     between tiles — and would still read a stale value inside the timeout. */
  const openRef = useRef(false);
  useEffect(() => {
    openRef.current = hover !== null;
  }, [hover]);

  const open = useCallback(
    (index, target) => {
      if (suppressed || !target) return;
      const rect = target.getBoundingClientRect();
      clearTimer();

      // Once a preview is up, moving to the next tile swaps it instantly. The
      // delay guards only the FIRST one, so sweeping the mouse across the
      // strip on the way somewhere else does not flash five panels.
      if (openRef.current) {
        setHover({ index, rect });
        return;
      }
      timerRef.current = setTimeout(() => setHover({ index, rect }), OPEN_DELAY_MS);
    },
    [suppressed]
  );

  const handlers = useCallback(
    (index) => ({
      onMouseEnter: (e) => open(index, e.currentTarget),
      onMouseLeave: close,
      onFocus: (e) => open(index, e.currentTarget),
      onBlur: close,
    }),
    [open, close]
  );

  return {
    hoverIndex: hover?.index ?? null,
    hoverRect: hover?.rect ?? null,
    handlers,
    closeHover: close,
    lightboxIndex: lightbox,
    openLightbox: (index) => {
      close();
      setLightbox(index);
    },
    setLightboxIndex: setLightbox,
    closeLightbox: () => setLightbox(null),
  };
};

export default useImagePeek;
